import React, { useState, useEffect } from "react";
import { Card, Form, Input, Button, Typography, Space, message, AutoComplete } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { KeyOutlined, SafetyOutlined } from "@ant-design/icons";
import loginIdsStorage from "../../storage/loginIdsStorage";

const { Title, Text } = Typography;

/**
 * Página de login da aplicação.
 *
 * Permite que o usuário faça login usando ID e Secret.
 * Utiliza OAuth 2.0 client credentials para autenticação.
 */
interface LoginLocationState {
  forbidden?: boolean;
}

const Login: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [savedIds, setSavedIds] = useState<string[]>([]);
  const locationState = (location.state as LoginLocationState | null) ?? {};

  // Carregar IDs salvos ao montar o componente
  useEffect(() => {
    const ids = loginIdsStorage.getIds();
    setSavedIds(ids);
  }, []);

  useEffect(() => {
    if (locationState.forbidden) {
      message.error("Sua credencial não possui permissão para acessar o SupReq.");
    }
  }, [locationState.forbidden]);

  /**
   * Handler para submeter o formulário de login.
   */
  const handleSubmit = async (values: { id: string; secret: string }) => {
    try {
      setLoading(true);
      await login({ id: values.id, secret: values.secret });

      // Salvar ID para autocompletar
      loginIdsStorage.addId(values.id);
      setSavedIds(loginIdsStorage.getIds());

      message.success("Login realizado com sucesso!");
      navigate("/");
    } catch (error: unknown) {
      console.error("Erro no login:", error);

      const status = (error as { response?: { status?: number } })?.response?.status;
      const isForbidden =
        status === 403 ||
        (error instanceof Error && error.message === "FORBIDDEN");

      if (isForbidden) {
        message.error("Sua credencial não possui permissão para acessar o SupReq.");
        return;
      }

      message.error("Erro ao realizar login. Verifique suas credenciais.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #4DAB4B 0%, #29784c 100%)",
        padding: "20px",
      }}
    >
      <Card
        style={{
          width: "100%",
          maxWidth: 400,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div style={{ textAlign: "center" }}>
            <Title level={2} style={{ marginBottom: 8, color: "#4DAB4B" }}>
              SupReq
            </Title>
            <Text type="secondary">O melhor amigo do suporte</Text>
          </div>

          <Form form={form} name="login" onFinish={handleSubmit} layout="vertical" size="large" autoComplete="off">
            <Form.Item name="id" label="ID" rules={[{ required: true, message: "Por favor, insira o ID!" }]}>
              <AutoComplete options={savedIds.map((id) => ({ value: id }))} filterOption={(inputValue, option) => option?.value?.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1} placeholder="ID do cliente" allowClear>
                <Input prefix={<KeyOutlined />} autoComplete="username" />
              </AutoComplete>
            </Form.Item>

            <Form.Item name="secret" label="Secret" rules={[{ required: true, message: "Por favor, insira o Secret!" }]}>
              <Input.Password prefix={<SafetyOutlined />} placeholder="Secret do cliente" autoComplete="current-password" />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                style={{
                  backgroundColor: "#4DAB4B",
                  borderColor: "#4DAB4B",
                  height: "45px",
                }}
              >
                ENTRAR
              </Button>
            </Form.Item>
          </Form>
        </Space>
      </Card>
    </div>
  );
};

export default Login;
